import * as React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps = useRender.ComponentProps<"button"> &
  React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants>;

// Composition uses Base UI's render-prop (`useRender`/`mergeProps`), the
// same primitive already used throughout components/ui/sidebar.tsx — not
// Radix's asChild/Slot, which this project doesn't depend on anywhere.
// Callers that need Button to render as something other than <button>
// (e.g. a react-router Link) pass `render={<Link to="..." />}` instead of
// an asChild boolean wrapping a single child.
//
// Must be React.forwardRef: base-ui primitives that render Button via
// `render={<Button ... />}` (e.g. DropdownMenuTrigger) attach a ref to
// register the DOM node as the floating-ui anchor. Without forwardRef,
// React silently drops that ref on a plain function component, so
// floating-ui never learns which element is the trigger — its
// outside-press dismiss logic then treats a click on the trigger itself
// as "outside", closing the menu the instant it opens.
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ className, variant, size, render, ...props }, ref) {
    return useRender({
      defaultTagName: "button",
      ref,
      props: mergeProps<"button">(
        {
          className: cn(buttonVariants({ variant, size, className })),
        },
        props,
      ),
      render,
    });
  },
);

export { Button, buttonVariants };
